import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ideaService } from '../../../services/ideas.service';
import Alert from '../../UI/Alert/Alert';
import styles from './IdeaDetails.module.css';
import { IoIosArrowBack } from 'react-icons/io';
import { FiEdit } from 'react-icons/fi';
import DateTime from '../../UI/Table/DateTime/DateTime';
import UserCell from '../../UI/Table/UserCell/UserCell';
import ColoredText from '../../UI/Table/ColoredText/ColoredText';
import Button from '../../UI/Button/Button';
import PermissionChecker from '../../UI/PermissionChecker/PermissionChecker';
import { useSelector } from 'react-redux';
import { selectAuth } from '../../../redux/slices/auth';
import { PERMISSIONS } from '../../../shared/permission';
import IdeasEditForm from '../IdeasEditForm/IdeasEditForm';
import { tagService } from '../../../services/tags.service';
import IdeaPdfViewer from './IdeaPdfViewer/IdeaPdfViewer';
import IdeaComments from './IdeaComments/IdeaComments';

const IdeaDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useSelector(selectAuth);
    const { t } = useTranslation('common');
    const { t: tValidation } = useTranslation('validation');
    const [idea, setIdea] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [tagsList, setTagsList] = useState([]);
    const [refreshKey, setRefreshKey] = useState(0);
    const [fileData, setFileData] = useState(null);
    const [isFileLoading, setIsFileLoading] = useState(false);
    const [locked, setLocked] = useState(false);

    useEffect(() => {
        const fetchIdea = async () => {
            setIsLoading(true);
            try {
                const data = await ideaService.getIdeaById(id);
                setIdea(data);
                const endDate = data?.event?.end_date ?? data?.end_date ?? null;
                if (endDate) setLocked(new Date() > new Date(endDate));
            } catch (err) {
                console.error('Error fetching idea details:', err);
                setError(err.message || 'Failed to fetch idea');
            } finally {
                setIsLoading(false);
            }
        };

        if (id) {
            fetchIdea();
        }
    }, [id, refreshKey]);

    useEffect(() => {
        const fetchFile = async () => {
            setIsFileLoading(true);
            try {
                const data = await ideaService.getIdeaFile(id);
                setFileData(data);
            } catch (err) {
                console.error('Error fetching idea file:', err);
            } finally {
                setIsFileLoading(false);
            }
        };

        if (id) {
            fetchFile();
        }
    }, [id, refreshKey]);

    useEffect(() => {
        const loadTags = async () => {
            try {
                const response = await tagService.getTagsAll();
                setTagsList(response.tags ?? []);
            } catch (error) {
                console.log(error);
            }
        };
        loadTags();
    }, []);

    const handleBack = () => {
        navigate('/crm/ideas');
    };

    const handleIdeaUpdated = () => {
        setRefreshKey(prev => prev + 1);
    };

    if (isLoading) {
        return <div className={styles.loadingContainer}>{t('loading', 'Loading...')}</div>;
    }

    if (error) {
        return (
            <div className={styles.errorContainer}>
                <div className={styles.errorText}>{error}</div>
                <button className={styles.backButton} onClick={handleBack}>
                    <IoIosArrowBack /> {t('ideas.back')}
                </button>
            </div>
        );
    }

    if (!idea) {
        return <div className={styles.errorContainer}>{t('ideas.no_rows')}</div>;
    }

    const colorMap = {
        'ready_to_review': 'green',
        'draft': 'yellow',
        'archived': 'gray',
    };

    const statuses = [
        { value: 'ready_to_review', label: t('ideas.statuses.ready_to_review') },
        { value: 'draft', label: t('ideas.statuses.draft') },
        { value: 'archived', label: t('ideas.statuses.archived') },
    ];

    return (
        <div className={styles.pageContainer}>
            <div className={styles.card}>
                <div className={styles.actionsRow}>
                    <button className={styles.backButtonMock} onClick={handleBack}>
                        <IoIosArrowBack /> {t('ideas.back_to_list')}
                    </button>
                    <PermissionChecker permissions={[PERMISSIONS.IDEAS.EDIT]}>
                        {!locked && user?.id === idea.created_by?.id && (
                            <Button
                                variant="primary"
                                onClick={() => setIsEditModalOpen(true)}
                                className={styles.editButtonMock}
                                size="small"
                            >
                                <FiEdit /> {t('ideas.edit')}
                            </Button>
                        )}
                    </PermissionChecker>
                </div>
                {locked && (
                    <Alert type="error">
                        <p>{tValidation('editing_is_closed_for_this_event')}</p>
                    </Alert>
                )}

                <div className={styles.headerMain}>
                    <h1 className={styles.title}>{idea.title}</h1>
                    <div className={styles.statusBadge}>
                        <ColoredText
                            value={t(`ideas.statuses.${idea.status}`)}
                            colorMap={colorMap}
                            colorCondition={idea.status}
                        />
                    </div>
                </div>

                <div className={styles.metadataRow}>
                    <span className={styles.metaItem}>№{idea.id}</span>
                    <span className={styles.metaSeparator}>•</span>
                    <div className={styles.metaItem}>
                        <span className={styles.metaLabel}>{t('ideas.creator')}:</span>
                        <UserCell user={idea.created_by} />
                    </div>
                    <span className={styles.metaSeparator}>•</span>
                    <div className={styles.metaItem}>
                        <span className={styles.metaLabel}>{t('ideas.created_at')}:</span>
                        <DateTime date={idea.created_at} className={styles.metaDate} view="line" />
                    </div>
                </div>

                <div className={styles.tagsRow}>
                    <span className={styles.tagsLabel}>{t('ideas.tags_label')}:</span>
                    <div className={styles.tagsContainer}>
                        {idea.tags && idea.tags.length > 0 ? (
                            idea.tags.map((tag, index) => (
                                <span key={index} className={styles.tagChip}>{tag}</span>
                            ))
                        ) : (
                            <span className={styles.metaValue}>-</span>
                        )}
                    </div>
                </div>

                <div className={styles.descriptionSection}>
                    <h2 className={styles.sectionTitle}>{t('ideas.description')}</h2>
                    <p className={styles.descriptionText}>{idea.description}</p>
                </div>
            </div>

            <IdeaPdfViewer
                fileData={fileData}
                isLoading={isFileLoading}
                ideaId={id}
                onUpdateSuccess={handleIdeaUpdated}
                locked={locked}
                onLocked={() => setLocked(true)}
                isOwner={user?.id === idea.created_by?.id}
            />

            <IdeaComments ideaId={id} />

            {isEditModalOpen && (
                <IdeasEditForm
                    isOpen={isEditModalOpen}
                    onClose={() => setIsEditModalOpen(false)}
                    onSuccess={handleIdeaUpdated}
                    idea={idea}
                    tagsList={tagsList}
                    statuses={statuses}
                    locked={locked}
                    onLocked={() => setLocked(true)}
                />
            )}
        </div>
    );
};

export default IdeaDetails;
